import React, {useState, useEffect, useContext} from "react";
import {Checkbox, Button} from "antd";
import DashboardWidget from "./DashboardWidget";
import axios from "../common/axios";
import MailIcon from '@mui/icons-material/Mail';
import {UserContext} from "../common/UserContext";
import {BrandContext} from "../common/BrandContext";
import {ContentContext} from "../common/ContentContext";
import GenericDialog from "../common/GenericDialog";
import Survey from "../components/Survey";
import dayjs from "dayjs";

const surveyRegex = /.*(https:\/\/widget\.surveymonkey\.com\/collect\/website\/js\/.+\.js).*/;
let daysUntilExpiry;

const WelcomeDashboardWidgetDevelopment = props => {
    const {...other} = props;
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const {content} = useContext(ContentContext);
    const [limitedProductAcknowledged, setLimitedProductAcknowledged] = useState(false);
    const [isShowSyllabusNotes, setShowSyllabusNotes] = useState(false);
    const [isShowDialogMessages, setShowDialogMessages] = useState(false);
    const [loginMessages, setLoginMessages] = useState([]);
    const [dialogMessages, setDialogMessages] = useState([]);
    const [dialogMessageIndex, setDialogMessageIndex] = useState(-1);
    const [syllabusGroupTutorials, setSyllabusGroupTutorials] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [isDialogLoading, setDialogLoading] = useState(false);
    const [fromLogin, setFromLogin] = useState(false);
    const [showLimitedProductDialog, setShowLimitedProductDialog] = useState(false);

    const loadSyllabusGroupsTutorials = () => {
        setLoading(true);
        axios.get('/web/ui/tutorial/syllabusGroup/tutorialContent/note')
            .then(response => {
                if (response.data !== undefined) {
                    setSyllabusGroupTutorials(response.data);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }

    const loadLoginMessages = () => {
        setLoading(true);
        axios.get('/web/ui/message/loginMessages')
            .then(response => {
                if (response.data !== undefined) {
                    const locLoginMessages = response.data.filter((message) => !message.useDialog);
                    const locDialogMessages = response.data.filter((message) => message.useDialog);
                    setLoginMessages(locLoginMessages);
                    setDialogMessages(locDialogMessages);

                    setDialogMessageIndex(locDialogMessages.length > 0 ? 0 : -1);
                } else {
                    setDialogMessageIndex(-1);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }

    const toggleDoNotShow = (event) => {
        setLimitedProductAcknowledged(event.target.checked);
    };

    const checkFromLogin = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const fromLoginParam = queryParams.get('fromLogin');
        if (fromLoginParam) {
            setFromLogin(JSON.parse(fromLoginParam));
        }
    }

    const checkLimitedProductAcknowledged = () => {
        setLoading(true);
        axios.get('/web/ui/dashboard/checkLimitedUserProductAcknowledgement')
            .then(response => {
                setShowLimitedProductDialog(!response.data);
                setLoading(false);
            }).catch(error => {
            setLoading(false);
            console.error('error acknowledged limited product: ', error);
        });
    }

    useEffect(() => {
        checkFromLogin();
        loadSyllabusGroupsTutorials();
        loadLoginMessages();
        if (user.limitedProduct) {
            checkLimitedProductAcknowledged();
        }
        if (user.accountExpiringSoon) {
            let dateToday = dayjs().startOf('day'),
                expiryDate = dayjs(user.expiryDate);
            daysUntilExpiry = expiryDate.diff(dateToday, 'days');
        }
    }, []);

    useEffect(() => {
        setShowDialogMessages(fromLogin && dialogMessageIndex >= 0);
    }, [fromLogin, dialogMessageIndex]);


    const updateLimitedProductAcknowledged = () => {
        setLoading(true);
        axios.post('/web/ui/dashboard/updateLimitedProduct', null, {params: {limitedProductAcknowledged: limitedProductAcknowledged}})
            .then(() => {
                setLoading(false);
            }).catch(error => {
            setLoading(false);
            console.error('error updating limited product acknowledged', error);
        });
    }

    const markUserMessageRead = async () => {
        if (!dialogMessages || dialogMessageIndex < 0) {
            return Promise.reject('There are no messages');
        }

        setDialogLoading(true);
        try {
            const response = await axios.post('/web/ui/message/markUserMessageRead', null, {params: {messageId: dialogMessages[dialogMessageIndex].messageId}});
            if (response.data) {
                loadLoginMessages();
            }
        } catch (error) {
            console.error('error marking message as read', error);
        } finally {
            setDialogLoading(false);
        }
    }

    const getGreeting = () => {
        if(brandConfig.welcomeTitle !== 'Welcome') return brandConfig.welcomeTitle;
        const hours = new Date().getHours();
        if (hours >= 4 && hours < 12) return 'Good morning';
        if (hours >= 12 && hours < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const contentBlocks = [];

    if (brandConfig.welcomeText) {
        contentBlocks.push(
            <div key="welcome-text" className={'ch-dashboard-welcome-note'}>
                {brandConfig.welcomeText}
            </div>
        );
    }

    if (user.limitedProduct) {
        contentBlocks.push(
            <div key="limited-product" className={'ch-dashboard-welcome-note'}>
                <div className={'ch-dashboard-warning-text'}>
                    You have activated a student product in a teacher account. Some teacher specific features will be disabled.
                </div>
                {showLimitedProductDialog && fromLogin &&
                    <GenericDialog open={false} title="Student resource on teacher account" onClose={() => setShowLimitedProductDialog(false)} buttons={[{name: 'Close'}]}>
                        <div>
                            <div>
                                You have activated a student resource. If you would like to get access to the full teacher version instead, please contact your Cambridge Representative or email
                                &nbsp;<a href={`mailto:${brandConfig.generalSchoolsEmail}`}>{brandConfig.generalSchoolsEmail}</a>
                            </div>
                            <div style={{padding: 'var(--small-spacing-unit)'}}>
                                <Checkbox onChange={toggleDoNotShow}>Please do not show me this again</Checkbox>
                            </div>
                        </div>
                    </GenericDialog>
                }
            </div>
        );
    }

    if (user.accountExpiringSoon) {
        contentBlocks.push(
            <div key="expiry-warning" className={'ch-dashboard-welcome-note'}>
                <div className={'ch-dashboard-warning-text'}>{`Your account will expire in ${daysUntilExpiry} days (${user.dateFormat.format(user.expiryDate)}).`}</div>
                <div>Renew your subscription to continue to access this site.</div>
            </div>
        );
    }

    if (!content?.syllabusList?.length) {
        contentBlocks.push(
            <div key="no-courses" className={'ch-dashboard-welcome-note'}>
                <div className={'ch-dashboard-important-text'}>You have no active courses.</div>
                <div>Please contact your teacher or school coordinator to activate your courses.</div>
            </div>
        );
    }

    if (syllabusGroupTutorials?.length > 0) {
        contentBlocks.push(
            <div key="tutorials">
                {syllabusGroupTutorials.map((tutorial) => (
                    <div key={tutorial.tutorialId} className={'ch-dashboard-welcome-note'}>
                        {tutorial.showTutorialName && tutorial.tutorialName &&
                            <div className={'ch-dashboard-welcome-note-title'}>{tutorial.tutorialName}</div>
                        }
                        <div dangerouslySetInnerHTML={{__html: tutorial.content}}/>
                    </div>
                ))}
            </div>
        );
    }

    if (loginMessages?.length > 0) {
        contentBlocks.push(
            <div key="login-messages" className={'ch-dashboard-welcome-note'}>
                {loginMessages.map((message) => (
                    <div key={message.messageId} className={'ch-login-message'}>
                        <div className={'ch-login-message-subject'}>{message.subject}</div>
                        <div className={'ch-login-message-body'} dangerouslySetInnerHTML={{__html: message.messageBody}}/>
                    </div>
                ))}
            </div>
        );
    }

    if (dialogMessages?.length > 0 && dialogMessageIndex >= 0) {
        const currentMessage = dialogMessages[dialogMessageIndex];
        contentBlocks.push(
            <div key="dialog-messages" className={'ch-dashboard-welcome-note'}>
                <div className={'ch-dashboard-important-text'}>
                    <MailIcon className={'margin--s'}/><span>You have {dialogMessages.length} {dialogMessages.length > 1 ? 'messages' : 'message'}</span>&nbsp;
                    <Button type="primary" onClick={() => setShowDialogMessages(true)} style={{display: 'inline-block'}}>View</Button>
                </div>
                <GenericDialog
                    open={isShowDialogMessages}
                    title={currentMessage.subject}
                    fullWidth={true}
                    isLoading={isDialogLoading}
                    icon={<MailIcon className={'margin--s'} style={{fontSize: '30px'}}/>}
                    onClose={() => setShowDialogMessages(false)}
                    buttons={[{
                        name: 'Close'
                    }, {
                        name: 'Mark as read', callback: markUserMessageRead
                    }, {
                        name: 'Previous', hidden: dialogMessages.length <= 1, extraProps: {disabled: dialogMessageIndex <= 0}, callback: () => setDialogMessageIndex(dialogMessageIndex - 1)
                    }, {
                        name: 'Next', hidden: dialogMessages.length <= 1, extraProps: {disabled: dialogMessageIndex >= dialogMessages.length - 1}, callback: () => setDialogMessageIndex(dialogMessageIndex + 1)
                    }]}
                >
                    <div className={'ch-login-message'}>
                        <div className={'ch-login-message-body'} dangerouslySetInnerHTML={{__html: currentMessage.messageBody.replace(surveyRegex, '')}}/>
                    </div>
                    {currentMessage.surveyUrl &&
                        <div className={'ch-login-survey'}>
                            <Survey surveyUrl={currentMessage.surveyUrl}/>
                        </div>
                    }
                </GenericDialog>
            </div>
        );
    }

    if (content?.currentSyllabus?.syllabusNotesAbbr) {
        contentBlocks.push(
            <div key="syllabus-notes" className={'ch-dashboard-welcome-note'}>
                <div className={'ch-dashboard-important-text'}>
                    <div className="padding--m" dangerouslySetInnerHTML={{__html: content.currentSyllabus.syllabusNotesAbbr}}/>
                    <Button type="primary" onClick={() => setShowSyllabusNotes(true)}>View</Button>
                </div>
                {content.currentSyllabus.syllabusNotes && (
                    content.currentSyllabus.syllabusNotes.length > 100
                        ? <GenericDialog open={isShowSyllabusNotes} title="Notes" onClose={() => setShowSyllabusNotes(false)} buttons={[{name: 'Close'}]}>
                            <div dangerouslySetInnerHTML={{__html: content.currentSyllabus.syllabusNotes}}/>
                        </GenericDialog>
                        : <div>
                            <div className={'ch-dashboard-important-text'}>
                                <div dangerouslySetInnerHTML={{__html: content.currentSyllabus.syllabusNotesAbbr}}/>
                            </div>
                            <div dangerouslySetInnerHTML={{__html: content.currentSyllabus.syllabusNotes}}/>
                        </div>
                )}
            </div>
        );
    }

    return (
        <>
            <div
                className="ch-dashboard-title-with-icon"
                style={contentBlocks.length === 0 ? {marginBottom: '1rem', width: '100%'} : {}}
            >
                <svg className="ch-icon-medium" viewBox="0 0 128 128">
                    <use href="#welcome-icon"></use>
                </svg>
                <h2>{getGreeting()}, {user.firstName}!</h2>
            </div>

            {(isLoading || contentBlocks.length > 0) &&
                <DashboardWidget
                    isLoading={isLoading}
                    {...other}>
                    <div className={'ch-dashboard-blk-body ch-welcome-blk-body'}>
                        {contentBlocks}
                    </div>
                </DashboardWidget>
            }
        </>
    );
}

export default WelcomeDashboardWidgetDevelopment;